import { gql } from '@apollo/client';
import { ScenarioGroupSummary } from 'src/graphql';

export type ContentPackOwnerOrganisation = {
  id: string;
  name: string;
  logoUrl?: string | null;
  organisationType?: string | null;
  parentId?: string | null;
};

export type ContentManagementScenarioPackSummary = ScenarioGroupSummary & {
  organisation?: ContentPackOwnerOrganisation | null;
};

export type ContentManagementControlFrameworkSummary = {
  id: string;
  name: string;
  shortname?: string | null;
  author?: string | null;
  organisation?: ContentPackOwnerOrganisation | null;
  description?: string | null;
  featureKey?: string | null;
  version?: string | null;
  publishDate?: string | null;
  versionNumber?: number | null;
  isCurrentVersion?: boolean | null;
  versionCreatedBy?: string | null;
  controlCount?: number | null;
  frameworkControls?: Array<{
    id: string;
    name?: string | null;
    shortname?: string | null;
    control_identifier?: string | null;
    strategy?: string | null;
    description?: string | null;
    controlFramework?: {
      shortname?: string | null;
      author?: string | null;
      description?: string | null;
    } | null;
  }>;
  meta?: {
    createdAt?: string | null;
    updatedAt?: string | null;
  } | null;
  versions?: Omit<ContentManagementControlFrameworkSummary, 'versions'>[];
};

export const CONTENT_MANAGEMENT_SCENARIO_PACKS = gql`
  query contentManagementScenarioPacks {
    contentManagementScenarioPacks {
      id
      name
      description
      author
      organisation {
        id
        name
        logoUrl
        organisationType
        parentId
      }
      authorLogoPath
      category
      featureKey
      isSystem
      versionNumber
      isCurrentVersion
      versionCreatedBy
      meta {
        createdAt
        updatedAt
      }
      versions {
        id
        name
        versionNumber
        isCurrentVersion
        versionCreatedBy
        organisation {
          id
          name
          logoUrl
          organisationType
          parentId
        }
        scenarios {
          id
        }
        meta {
          createdAt
          updatedAt
        }
      }
      scenarios {
        id
        name
        description
        ownerName
        source
        event
        consequence
        riskNarrative
        frequencyNote
        managementStrategy
        managementNote
        consequenceNote
        lowerFormula
        upperFormula
        status
        riskIdentifiedDate
        reviewDate
        assessment {
          id
        }
        baseRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
          lower
          upper
        }
        projectedRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
          lower
          upper
        }
        liveRisk {
          frequencyTimes
          frequencyYears
          expectedLoss
          lower
          upper
        }
        meta {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export interface ContentManagementScenarioPacksQuery {
  contentManagementScenarioPacks: ContentManagementScenarioPackSummary[];
}

export const CONTENT_MANAGEMENT_CONTROL_FRAMEWORKS = gql`
  query contentManagementControlFrameworks {
    contentManagementControlFrameworks {
      id
      name
      shortname
      description
      author
      organisation {
        id
        name
        logoUrl
        organisationType
        parentId
      }
      featureKey
      version
      publishDate
      versionNumber
      isCurrentVersion
      versionCreatedBy
      controlCount
      meta {
        createdAt
        updatedAt
      }
      versions {
        id
        name
        shortname
        author
        organisation {
          id
          name
          logoUrl
          organisationType
          parentId
        }
        version
        publishDate
        versionNumber
        isCurrentVersion
        versionCreatedBy
        controlCount
        frameworkControls {
          id
        }
        meta {
          createdAt
          updatedAt
        }
      }
      frameworkControls {
        id
        name
        shortname
        control_identifier
        strategy
        description
        controlFramework {
          shortname
          author
          description
        }
      }
    }
  }
`;

export interface ContentManagementControlFrameworksQuery {
  contentManagementControlFrameworks: ContentManagementControlFrameworkSummary[];
}
